import type { Schemas } from "#shopware"

function isNeverVisible(entity: { visibility?: { mobile?: boolean; tablet?: boolean; desktop?: boolean } }): boolean {
    const v = entity.visibility
    return !!v && v.mobile === false && v.tablet === false && v.desktop === false
}

// A block/section hidden on every breakpoint still ships its resolved data - drop it.
function stripInvisibleContent(cmsPage: Schemas["CmsPage"] | null | undefined): void {
    if (!cmsPage?.sections) return
    cmsPage.sections = cmsPage.sections.filter((section) => {
        if (isNeverVisible(section)) return false
        if (section.blocks) {
            section.blocks = section.blocks.filter((block) => !isNeverVisible(block))
        }
        return true
    })
}

function forEachSlot(cmsPage: Schemas["CmsPage"] | null | undefined, visitor: (slot: Schemas["CmsSlot"]) => void): void {
    for (const section of cmsPage?.sections ?? []) {
        for (const block of section.blocks ?? []) {
            for (const slot of block.slots ?? []) visitor(slot)
        }
    }
}

function deleteFields<T extends object>(obj: T, keys: (keyof T)[]): void {
    for (const key of keys) Reflect.deleteProperty(obj, key)
}

// hero-slider/slider-base/branded-product-slider resolve slide media via their own backend
// Criteria (HeroSliderResolver/SliderBaseResolver/BrandedProductSliderResolver::collect()),
// independent of the request's includes - so the full ~50-field MediaEntity ships regardless
// of what the frontend asks for. Only path/alt/translated and metaData (the intrinsic
// dimensions MiscImage reserves its box with) are ever read, so this strips it down after
// the fact instead.
function stripSlideMedia(item: Record<string, unknown>, keys: string[]): void {
    for (const key of keys) {
        const media = item[key] as Record<string, unknown> | undefined
        if (media) {
            item[key] = { path: media.path, alt: media.alt, translated: media.translated, metaData: media.metaData }
        }
    }
}

// translated duplicates config byte-for-byte; apiAlias is never read. Both dead on every slot.
function stripUniversallyDeadFields(slot: Schemas["CmsSlot"]): void {
    deleteFields(slot as Record<string, unknown>, ["translated", "apiAlias"])
}

// Each config field is `{ source, value }`; source only matters to the unused useCmsElementConfig
// composable, so it's dead everywhere. Recurses since some fields nest further ElementConfig arrays.
function stripElementConfigSource(value: unknown): void {
    if (Array.isArray(value)) {
        value.forEach(stripElementConfigSource)
        return
    }
    if (value == null || typeof value !== "object") return
    const obj = value as Record<string, unknown>
    if ("source" in obj && "value" in obj) {
        Reflect.deleteProperty(obj, "source")
        stripElementConfigSource(obj.value)
    } else {
        Object.values(obj).forEach(stripElementConfigSource)
    }
}

type PayloadStripper = (slot: never) => void

// Per-element dead fields beyond the universal translated duplicate - see homepage-visible-elements.md.
const elementPayloadStrippers: Partial<Record<string, PayloadStripper>> = {
    // Navigation comes from injected mainNavigation, not the slot - data is dead.
    "category-navigation": ((slot: { data?: unknown }) => {
        slot.data = null
    }) as PayloadStripper,

    // aspectRatio/image/logo are dead in every bannerType branch; other fields are branch-specific.
    "hero-banner": ((slot: Schemas["CmsElementHeroBanner"]) => {
        if (slot.config) deleteFields(slot.config as Record<string, unknown>, ["aspectRatio", "image", "logo"])
    }) as PayloadStripper,

    // Reads only data.elements; config is unused, ruleId/linkedCategories/buttonTargetExtern/title per slide are dead.
    "hero-slider": ((slot: Schemas["CmsElementHeroSlider"]) => {
        deleteFields(slot as Record<string, unknown>, ["config"])
        for (const slide of Object.values(slot.data?.elements ?? {})) {
            deleteFields(slide as Record<string, unknown>, ["ruleId", "linkedCategories", "buttonTargetExtern", "title"])
            stripSlideMedia(slide as Record<string, unknown>, ["image", "logo"])
        }
    }) as PayloadStripper,

    // Only these are read; the rest are unused stock swiper settings.
    "product-slider": ((slot: Schemas["CmsElementProductSlider"]) => {
        const config = slot.config
        if (config) {
            slot.config = {
                title: config.title,
                promotion: config.promotion,
                productQty: config.productQty,
                progressBar: config.progressBar,
                framed: config.framed,
                accentColor: config.accentColor,
            } as Schemas["CmsElementProductSlider"]["config"]
        }
    }) as PayloadStripper,

    // Only tab.name and the first tab's data.products are used; entityCollection etc. are admin-only.
    "product-tab-slider": ((slot: Schemas["CmsElementProductTabSlider"]) => {
        const tabs = slot.config?.tabs
        if (tabs) tabs.value = (tabs.value ?? []).map((tab) => ({ name: tab.name }))
    }) as PayloadStripper,

    // config.slides duplicates data.elements and is unread across all 9 sliderContent types.
    "slider-base": ((slot: Schemas["CmsElementSliderBase"]) => {
        if (slot.config) deleteFields(slot.config as Record<string, unknown>, ["slides"])
        for (const slide of Object.values(slot.data?.elements ?? {})) {
            stripSlideMedia(slide as Record<string, unknown>, ["media", "logo"])
        }
    }) as PayloadStripper,

    // Same config.slides pattern as slider-base; isActive/ruleId/urlNewTab per slide are also unread.
    "branded-product-slider": ((slot: Schemas["CmsElementBrandedProductSlider"]) => {
        if (slot.config) deleteFields(slot.config as Record<string, unknown>, ["slides"])
        for (const slide of Object.values(slot.data?.elements ?? {})) {
            deleteFields(slide as Record<string, unknown>, ["isActive", "ruleId", "urlNewTab"])
            stripSlideMedia(slide as Record<string, unknown>, ["media"])
        }
    }) as PayloadStripper,

    // config.media duplicates the resolved data.media; the rest are unread stock config fields.
    "image": ((slot: Schemas["CmsElementImage"]) => {
        if (slot.config) {
            deleteFields(slot.config as Record<string, unknown>, [
                "media", "product", "boxLayout", "verticalAlign", "horizontalAlign", "fetchPriorityHigh",
            ])
        }
    }) as PayloadStripper,

    // discoverNowButtonText duplicates learnMoreButtonText and is never read.
    "webflow-slider": ((slot: Schemas["CmsElementWebflowSlider"]) => {
        if (slot.config) deleteFields(slot.config as Record<string, unknown>, ["discoverNowButtonText"])
    }) as PayloadStripper,

    // config.sliderItems is the raw, unresolved category-ID list; data is what's actually rendered.
    "category-slider": ((slot: Schemas["CmsElementCategorySlider"]) => {
        if (slot.config) deleteFields(slot.config as Record<string, unknown>, ["sliderItems"])
    }) as PayloadStripper,

    // config.content duplicates data.content, which is what's actually rendered.
    "text": ((slot: Schemas["CmsElementText"]) => {
        if (slot.config) deleteFields(slot.config as Record<string, unknown>, ["content"])
    }) as PayloadStripper,
}

// Also used directly for the header/footer CmsPage from /store-api/layout-cms.
export function stripCmsPage(cmsPage: Schemas["CmsPage"] | null | undefined): Schemas["CmsPage"] | null | undefined {
    forEachSlot(cmsPage, (slot) => {
        stripUniversallyDeadFields(slot)
        elementPayloadStrippers[slot.type]?.(slot as never)
        // Runs last to also cover structures the per-type stripper already reduced.
        stripElementConfigSource(slot.config)
    })
    stripInvisibleContent(cmsPage)
    return cmsPage
}

export function stripCmsPayload(category: Schemas["Category"] | undefined): Schemas["Category"] | undefined {
    if (category) stripCmsPage(category.cmsPage)
    return category
}
